import React, { useState, useMemo } from 'react';
import { Search, MapPin, Link2, AlertTriangle, AlertCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { VStack, HStack } from '@/components/ui/Stack';
import { useNotificationStore } from '@/stores/notificationStore';
import { CventIntegrationService } from '@/services/CventIntegrationService';

/**
 * Subset of CventVenueSummary the modal needs. We don't import the server
 * type to keep the front-end self-contained.
 */
export interface CventSupplierOption {
  id: string;
  venueName: string;
  status?: string | null;
  address?: {
    city?: string | null;
    countryCode?: string | null;
    stateProvince?: string | null;
  } | null;
}

interface CventManualConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The shortlist row the user is fixing — used for context strings only. */
  shortlistVenueName: string;
  /** Event id for the API call. */
  eventId: string;
  /** Shortlist id for the API call. */
  shortlistId: string;
  /** All Cvent suppliers on this RFP — already loaded by the parent page. */
  cventSuppliers: CventSupplierOption[];
  /**
   * Map of cvent_supplier_id → the property name of the shortlist row it's
   * already connected to, for the "already connected to X" warning. Only
   * include entries for *other* shortlist rows (not the one the user is
   * fixing).
   */
  alreadyConnectedTo: Map<string, string>;
  /** Called after a successful connect so the parent can refresh the table. */
  onConnected: () => void;
}

/**
 * Format Cvent's raw status enum into a friendly label + Badge variant.
 */
function formatStatus(status: string | null | undefined): {
  label: string;
  variant: 'success' | 'warning' | 'error' | 'secondary';
} | null {
  if (!status) return null;
  const s = status.toLowerCase().replace(/[_\s]+/g, '_');
  if (['submitted_proposal', 'submitted', 'received'].includes(s)) {
    return { label: 'Submitted', variant: 'success' };
  }
  if (['turned_down', 'declined', 'rejected'].includes(s)) {
    return { label: 'Declined', variant: 'error' };
  }
  if (['awaiting_proposal', 'pending', 'draft', 'progress'].includes(s)) {
    return { label: 'Awaiting Response', variant: 'warning' };
  }
  if (['cancelled', 'canceled', 'expired'].includes(s)) {
    return { label: 'Cancelled', variant: 'secondary' };
  }
  // Unknown status — show raw value as a neutral chip
  return { label: status, variant: 'secondary' };
}

export function CventManualConnectModal({
  isOpen,
  onClose,
  shortlistVenueName,
  eventId,
  shortlistId,
  cventSuppliers,
  alreadyConnectedTo,
  onConnected,
}: CventManualConnectModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const notificationStore = useNotificationStore();

  // Filter the supplier list by the search term (matches name + city).
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return cventSuppliers;
    return cventSuppliers.filter((s) => {
      const name = (s.venueName || '').toLowerCase();
      const city = (s.address?.city || '').toLowerCase();
      return name.includes(q) || city.includes(q);
    });
  }, [cventSuppliers, searchTerm]);

  // Reset state on open
  React.useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setConnectingId(null);
      setError(null);
    }
  }, [isOpen]);

  async function handleConnect(supplier: CventSupplierOption) {
    const conflict = alreadyConnectedTo.get(supplier.id);
    if (conflict) {
      const confirmed = window.confirm(
        `"${supplier.venueName}" is currently connected to "${conflict}".\n\n` +
          `Connecting it to "${shortlistVenueName}" instead will leave "${conflict}" still pointing at the same Cvent supplier — you may want to disconnect that row afterwards.\n\n` +
          `Proceed?`
      );
      if (!confirmed) return;
    }

    setConnectingId(supplier.id);
    setError(null);
    try {
      const service = new CventIntegrationService();
      const result = await service.manuallyConnectShortlist({
        eventId,
        shortlistId,
        cventSupplierId: supplier.id,
      });
      notificationStore.success(
        `Connected "${shortlistVenueName}" to "${result.cventVenueName}"` +
          (result.cventStatus ? ` (${formatStatus(result.cventStatus)?.label ?? result.cventStatus})` : ''),
        { title: 'Connected to Cvent' }
      );
      onConnected();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to connect to Cvent supplier';
      setError(msg);
    } finally {
      setConnectingId(null);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Connect to Cvent supplier" size="lg">
      <VStack gap={4} className="min-h-[400px]">
        <div className="text-sm text-gray-600">
          Pick the Cvent supplier that corresponds to{' '}
          <span className="font-medium text-gray-900">{shortlistVenueName}</span>.
          The auto-matcher couldn&apos;t connect them — usually because the name or
          coordinates don&apos;t line up between Cvent and our database.
        </div>

        <Input
          placeholder="Search by name or city…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
          leftIcon={<Search className="w-4 h-4" />}
        />

        {error && (
          <div className="bg-error-50 border border-error-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error-700">{error}</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto border rounded-md min-h-[300px] max-h-[450px]">
          {filtered.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-2 py-12">
              <Search className="w-8 h-8 opacity-20" />
              <p className="text-sm">
                {cventSuppliers.length === 0
                  ? 'No Cvent suppliers on this RFP yet.'
                  : `No suppliers match "${searchTerm}"`}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((supplier) => {
                const isConnecting = connectingId === supplier.id;
                const conflict = alreadyConnectedTo.get(supplier.id);
                const status = formatStatus(supplier.status);
                const cityCountry = [supplier.address?.city, supplier.address?.countryCode]
                  .filter(Boolean)
                  .join(', ');

                return (
                  <div
                    key={supplier.id}
                    className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <HStack gap={2} className="mb-1 flex-wrap">
                        <div className="font-medium text-gray-900 truncate">
                          {supplier.venueName || '(unnamed supplier)'}
                        </div>
                        {status && (
                          <Badge variant={status.variant} className="text-xs flex-shrink-0">
                            {status.label}
                          </Badge>
                        )}
                        {conflict && (
                          <Badge variant="warning" className="text-xs flex-shrink-0">
                            <AlertTriangle className="w-3 h-3 mr-1 inline" />
                            already connected to {conflict}
                          </Badge>
                        )}
                      </HStack>
                      {cityCountry && (
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{cityCountry}</span>
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={conflict ? 'secondary' : 'primary'}
                      leftIcon={<Link2 className="w-4 h-4" />}
                      isLoading={isConnecting}
                      disabled={!!connectingId && !isConnecting}
                      onClick={() => handleConnect(supplier)}
                    >
                      {isConnecting ? 'Connecting…' : 'Connect'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </VStack>
    </Modal>
  );
}
